import { inject, Injectable } from '@angular/core';
import { WSS_API_BASE, ARROW } from '../../constants/general.constants';
import { ToastrService } from 'ngx-toastr';
import { ArrowAliasService } from '../arrow-alias/arrow-alias.service';
import { ArrowAliasV2, DataSet } from '../../interfaces/arrow-alias.interface';
import { FileService } from '../file/file.service';
import { ArrowClient, CreateTableParams } from './arrow-client';
import { ArrowError } from './arrow-error';
import { ExecutionContext } from '../../interfaces/chunk/chunk-context.interface';
import { ArrowHelper } from '../../helpers/arrow.helper';
import { AppStateService } from '../app-state/app-state.service';
import { AuthenticationService } from '../authentication/authentication.service';
import {
  optionField,
  optionsOf,
  resolveQueryArgs,
  toTypedParams,
} from './query-params';

/**
 * Type alias for parameters to methods
 */
type ArrowParams = unknown[];

/**
 * Represents a service for managing Arrow Websocket connections.
 * This service handles connecting to Arrow aliases, sending and receiving messages,
 * and managing the active connections.
 */
@Injectable({
  providedIn: 'root',
})
export class ArrowWebsocketService {
  private readonly authService = inject(AuthenticationService);
  private readonly toastr = inject(ToastrService);
  private readonly arrowAliasService = inject(ArrowAliasService);
  private readonly fileService = inject(FileService);

  readonly #appState = inject(AppStateService);

  readonly #clients = new Map<string, ArrowClient>();
  #currentClient: ArrowClient | undefined;

  // ─────────────────────────────────────────────────────────────────────
  // ─── Do connection ───────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  public async connectToAliasNamed(
    aliasName: string,
    context?: ExecutionContext,
    standalone = false
  ): Promise<ArrowClient> {
    const projectId = this.#appState.projectId();
    return new Promise((resolve, reject) => {
      const cacheKey = `${aliasName}-${projectId}`;
      if (!standalone) {
        const existing = this.#clients.get(cacheKey);
        if (existing) {
          resolve(existing);
          return;
        }
      }
      // need to connect and return the connection
      this.connect([aliasName], context)
        .then(() => {
          if (this.#currentClient !== undefined) {
            const client = this.#currentClient;
            if (standalone) {
              this.#clients.delete(cacheKey);
              this.#currentClient = undefined;
            }
            resolve(client);
          } else {
            reject(new Error('failed to connect to alias'));
          }
        })
        .catch(() => {
          reject(new Error('failed to connect to alias'));
        });
    });
  }

  private validateConnectionArgs(args: ArrowParams, chunkContext?: ExecutionContext): string {
    if (this === undefined) {
      throw new Error('ArrowWebsocketService is undefined');
    }
    if (!args.length || typeof args[0] !== 'string' || !args[0].trim()) {
      const error = new Error('Invalid or missing alias name.');
      if (chunkContext) {
        chunkContext.addMessage('Invalid or missing alias name.', 'danger');
      }
      throw error;
    }
    return args[0] as string;
  }

  private validateAlias(aliasName: string): ArrowAliasV2 {
    if (aliasName === undefined) {
      throw new Error('Alias name must be provided');
    }

    const alias = this.getAliasByName(aliasName);
    if (!alias) {
      const error = new Error(`Alias "${aliasName}" not found. Please check that the alias exists and is configured correctly.`);
      this.toastr.error(`Alias "${aliasName}" not found`, 'Connection Error');
      throw error;
    }
    return alias;
  }

  private async setupNewConnection(alias: ArrowAliasV2, cacheKey: string, chunkContext?: ExecutionContext): Promise<void> {
    const token = this.getAuthToken();
    if (!token) {
      const error = new Error('Authentication token not available');
      if (chunkContext) {
        chunkContext.addMessage('Authentication token not available', 'danger');
      }
      throw error;
    }

    const url = this.buildDatafusionWebSocketUrl(alias.aliasId, token);
    const client = new ArrowClient(alias, url);

    this.#clients.set(cacheKey, client);
    this.#currentClient = client;
    client.wsClosedCallback = () => {
      this.#clients.delete(cacheKey);
      if (this.#currentClient === client) this.#currentClient = undefined;
    };
    await client.connect();
  }

  public async connect(args: ArrowParams, chunkContext?: ExecutionContext): Promise<void> {
    const aliasName = this.validateConnectionArgs(args, chunkContext);
    const alias = this.validateAlias(aliasName);
    const cacheKey = `${alias.name}-${alias.projectId}`;

    const existing = this.#clients.get(cacheKey);
    if (existing) {
      if (!existing.connected) {
        this.#clients.delete(cacheKey);
      } else {
        this.#currentClient = existing;
        return;
      }
    }

    try {
      await this.setupNewConnection(alias, cacheKey, chunkContext);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (chunkContext) {
        chunkContext.addMessage(`Connection error: ${errorMessage}`, 'danger');
      }
      throw error;
    }
  }

  private getAuthToken(): string | null {
    try {
      return this.authService.getAccessToken() || null;
    } catch (error) {
      console.error('Failed to get authentication token:', error);
      throw new Error('Authentication token not available');
    }
  }

  private buildDatafusionWebSocketUrl(aliasId: number, token: string): string {
    // Avoid regex /\/+$/ - vulnerable to ReDoS due to backtracking (typescript:S5852)
    const baseUrl = this.trimTrailingSlashes(WSS_API_BASE);
    const workflowId = this.#appState.workflowId();
    return `${baseUrl}/${ARROW}/${aliasId}?token=${encodeURIComponent(token)}&workflow_id=${workflowId}`;
  }

  /** Removes trailing slashes without regex to avoid ReDoS (typescript:S5852) */
  private trimTrailingSlashes(s: string): string {
    let result = s;
    while (result.endsWith('/')) {
      result = result.slice(0, -1);
    }
    return result;
  }

  // ─────────────────────────────────────────────────────────────────────
  // ─── Do conenction ───────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  public async disconnect(): Promise<void> {
    if (!this.#currentClient) {
      throw new Error('No active connection to disconnect');
    }
    await this.#currentClient.disconnect();
    this.#clients.delete(
      `${this.#currentClient.alias.name}-${this.#currentClient.alias.projectId}`
    );
    this.#currentClient = undefined;
  }

  // ─────────────────────────────────────────────────────────────────────
  // ─── Helpers ─────────────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────
  private getAliasByName(name: string): ArrowAliasV2 | undefined {
    return this.arrowAliasService
      .aliasV2List()
      .find((item: ArrowAliasV2) => item.name === name);
  }

  /**
   * Validates that a client connection exists
   * @param operationName Name of the operation for error messages
   * @param context Optional execution context for error messages
   * @throws Error if no active connection
   */
  private ensureClientConnected(operationName: string, context?: ExecutionContext): void {
    if (this.#currentClient === undefined) {
      const error = new Error(`No active connection to ${operationName}`);
      if (context) {
        context.addMessage(`No active connection to ${operationName}`, 'danger');
      }
      throw error;
    }
  }

  /**
   * Validates arguments array
   * @param args Arguments array to validate
   * @param minLength Minimum required length
   * @param operationName Name of the operation for error messages
   * @param context Optional execution context for error messages
   * @throws Error if arguments are invalid
   */
  private validateArguments(
    args: ArrowParams,
    minLength: number,
    operationName: string,
    context?: ExecutionContext
  ): void {
    if (!Array.isArray(args) || args.length < minLength) {
      const error = new Error(`Invalid arguments for ${operationName}`);
      if (context) {
        context.addMessage(`Invalid arguments for ${operationName}`, 'danger');
      }
      throw error;
    }
  }

  /**
   * Handles errors consistently across methods
   * @param error The error that occurred
   * @param operationName Name of the operation for error messages
   * @param context Optional execution context for error messages
   * @throws The original error after logging
   */
  private handleError(
    error: unknown,
    operationName: string,
    context?: ExecutionContext
  ): never {
    const errorMessage = error instanceof Error ? error.message : String(error);
    const code = error instanceof ArrowError ? error.code : undefined;
    if (context) {
      context.addMessage(
        `Error ${operationName}: ${errorMessage}${code ? ` [${code}]` : ''}`,
        'danger'
      );
    }
    throw error; // Rethrow to allow try/catch blocks to work
  }

  // ─────────────────────────────────────────────────────────────────────
  // ─── Send/Receive message ────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────
  // ─── Shared Methods ──────────────────────────────────────────────────
  // ─────────────────────────────────────────────────────────────────────

  public async createDataSet(
    args: ArrowParams,
    chunkContext: ExecutionContext
  ): Promise<void> {
    this.ensureClientConnected('create data set', chunkContext);
    this.validateArguments(args, 5, 'creating data set', chunkContext);

    try {
      return await this.#currentClient!.createDataSet(
        args[0] as string,
        args[1] as string,
        args[2] as string[],
        args[3] as number,
        args[4] as string
      );
    } catch (error: unknown) {
      this.handleError(error, 'creating data set', chunkContext);
    }
  }

  public async deleteDataSet(args: ArrowParams, context?: ExecutionContext): Promise<void> {
    this.ensureClientConnected('delete data set', context);
    this.validateArguments(args, 1, 'deleting data set', context);

    try {
      return await this.#currentClient!.deleteDataSet(args[0] as string);
    } catch (error: unknown) {
      this.handleError(error, 'deleting data set', context);
    }
  }

  public async createView(args: ArrowParams, context?: ExecutionContext): Promise<string> {
    this.ensureClientConnected('create view', context);
    this.validateArguments(args, 4, 'creating view', context);

    try {
      // Make sure we parse the numeric parameter correctly
      const expiresAfter = typeof args[2] === 'number' 
        ? args[2] 
        : parseInt(String(args[2]), 10);

      return await this.#currentClient!.createView(
        String(args[0]),
        String(args[1]),
        expiresAfter,
        String(args[3]),
        args.length > 4 ? String(args[4]) : undefined
      );
    } catch (error: unknown) {
      this.handleError(error, 'creating view', context);
    }
  }

  public async deleteView(args: ArrowParams, context?: ExecutionContext): Promise<void> {
    this.ensureClientConnected('delete view', context);
    this.validateArguments(args, 2, 'deleting view', context);

    try {
      return await this.#currentClient!.deleteView(args[0] as string, args[1] as string);
    } catch (error: unknown) {
      this.handleError(error, 'deleting view', context);
    }
  }

  public async listDataSets(context?: ExecutionContext): Promise<object[]> {
    this.ensureClientConnected('list data sets', context);

    try {
      return this.#currentClient!.listDataSets();
    } catch (error: unknown) {
      this.handleError(error, 'listing data sets', context);
    }
  }

  public async refreshDataSetList(): Promise<DataSet[]> {
    this.ensureClientConnected('refresh data set list');
    return this.#currentClient!.refreshDataSetList();
  }

  public async listCloud(args: ArrowParams): Promise<string[]> {
    this.ensureClientConnected('list cloud');
    return this.#currentClient!.listCloud(args[0] as string, args[1] as string, args[2] as string, args[3] as number);
  }

  public async listCloudDirs(args: ArrowParams): Promise<string[]> {
    this.ensureClientConnected('list cloud dirs');
    return this.#currentClient!.listCloudDirs(args[0] as string, args[1] as string);
  }

  public async cloudBasePathExists(args: ArrowParams): Promise<boolean> {
    this.ensureClientConnected('check cloud base path');
    return this.#currentClient!.cloudBasePathExists(args[0] as string, args[1] as string);
  }

  public async listViews(args: ArrowParams): Promise<object[]> {
    this.ensureClientConnected('list views');
    return this.#currentClient!.listViews(args[0] as string);
  }

  public async getSchema(args: ArrowParams): Promise<object> {
    this.ensureClientConnected('get schema');
    return this.#currentClient!.getSchema(args[0] as string);
  }

  public async registerSchema(args: ArrowParams): Promise<object> {
    this.ensureClientConnected('register schema');
    return this.#currentClient!.registerSchema(args[0] as string, args[1] as object[]);
  }

  public async initCache(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('init cache');
    return this.#currentClient!.initCache(args[0] as string);
  }

  public async performQuery(args: ArrowParams, context?: ExecutionContext): Promise<Uint8Array> {
    this.ensureClientConnected('perform query', context);
    this.validateArguments(args, 1, 'performing query', context);

    try {
      const { dataSetName, query, params } = resolveQueryArgs(args, 'performQuery');
      return this.#currentClient!.performQuery(dataSetName, query, false, params);
    } catch (error: unknown) {
      this.handleError(error, 'performing query', context);
    }
  }

  public async performQueryAsJson(args: ArrowParams, context?: ExecutionContext): Promise<string> {
    this.ensureClientConnected('query data as json', context);
    this.validateArguments(args, 1, 'performing query as JSON', context);

    try {
      const { dataSetName, query, params } = resolveQueryArgs(
        args,
        'performQueryAsJson'
      );

      // the asTable arg guarantees that the data is returned as arrow table
      const table = await this.#currentClient!.performQuery(
        dataSetName,
        query,
        true,
        params
      );

      const results = ArrowHelper.tableToRowJson(table as any);
      return JSON.stringify(results);
    } catch (error: unknown) {
      this.handleError(error, 'performing query as JSON', context);
    }
  }

  /**
   * Require the named string fields to be present after option/positional
   * resolution (EHQ-115).
   *
   * These methods historically had no arity check at all, so a missing argument
   * reached the client as `undefined` and surfaced as an opaque server-side
   * failure. The options form adds a second way to omit a field — `{ query }`
   * without `tableName` — so the check is worth having explicitly.
   */
  private requireStrings(
    fields: Record<string, unknown>,
    operationName: string,
    context?: ExecutionContext
  ): void {
    const missing = Object.entries(fields)
      .filter(([, v]) => typeof v !== 'string' || v.length === 0)
      .map(([k]) => k);
    if (missing.length > 0) {
      const message = `Invalid arguments for ${operationName}: missing or non-string ${missing.join(', ')}`;
      if (context) {
        context.addMessage(message, 'danger');
      }
      throw new Error(message);
    }
  }

  public async saveQueryAsTable(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('save query as table');
    const o = optionsOf(args);
    const query = optionField(o, 'query', args[0]) as string;
    const tableName = optionField(o, 'tableName', args[1]) as string;
    const params = toTypedParams(
      optionField(o, 'params', args[2]),
      'saveQueryAsTable'
    );
    this.requireStrings({ query, tableName }, 'saving query as table');
    return this.#currentClient!.saveQueryAsTable(query, tableName, params);
  }

  public async saveQueryToCloud(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('save query to cloud');
    const o = optionsOf(args);
    const query = optionField(o, 'query', args[0]) as string;
    const connectionName = optionField(o, 'connectionName', args[1]) as string;
    const path = optionField(o, 'path', args[2]) as string;
    const params = toTypedParams(
      optionField(o, 'params', args[3]),
      'saveQueryToCloud'
    );
    this.requireStrings(
      { query, connectionName, path },
      'saving query to cloud'
    );
    return this.#currentClient!.saveQueryToCloud(query, connectionName, path, params);
  }

  public async performSqlQuery(args: ArrowParams): Promise<Uint8Array> {
    this.ensureClientConnected('perform query');
    const { dataSetName, query, params } = resolveQueryArgs(args, 'performSqlQuery');
    return this.#currentClient!.performQuery(dataSetName, query, false, params);
  }

  public async saveSqlQueryAsTable(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('save sql query as table');
    const o = optionsOf(args);
    const query = optionField(o, 'query', args[0]) as string;
    const tableName = optionField(o, 'tableName', args[1]) as string;
    const params = toTypedParams(
      optionField(o, 'params', args[2]),
      'saveSqlQueryAsTable'
    );
    this.requireStrings({ query, tableName }, 'saving sql query as table');
    return this.#currentClient!.saveQueryAsTable(query, tableName, params);
  }


  public async doAction(args: ArrowParams): Promise<string> {
    this.ensureClientConnected('do action');
    return this.#currentClient!.doAction(args[0] as string, args[1] as string);
  }

  /**
   * Ingests a file from S3 storage directly into a dataset on the server.
   * Eliminates client-side memory usage for large file imports.
   */
  public async ingestFromStorage(args: ArrowParams): Promise<string> {
    this.ensureClientConnected('ingest from storage');
    const [sourceConnection, sourcePath, datasetName, basePath, options] = args;
    const argument = JSON.stringify({
      sourceConnection,
      sourcePath,
      datasetName,
      basePath,
      ...(options as object || {}),
    });
    return this.#currentClient!.doAction('IngestFromStorage', argument);
  }

  public async createPut(args: ArrowParams): Promise<number> {
    this.ensureClientConnected('create put');
    return this.#currentClient!.createPut(
      args[0] as string,
      args[1] as string,
      args[2] as unknown[],
      args[3] as number,
      args[4] as Buffer,
      args[5] as string
    );
  }

  public async initPut(args: ArrowParams): Promise<number> {
    this.ensureClientConnected('init put');
    return this.#currentClient!.initPut(args[0] as string);
  }

  public async continuePut(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('continue put');
    return this.#currentClient!.continuePut(args[0] as number, args[1] as Buffer);
  }

  public async endPut(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('end put');
    return this.#currentClient!.endPut(args[0] as number);
  }

  public async listTables(): Promise<object[]> {
    this.ensureClientConnected('list tables');
    return this.#currentClient!.listTables();
  }

  public async createTable(args: ArrowParams): Promise<object> {
    this.ensureClientConnected('create table');
    if (!Array.isArray(args) || args.length < 1) {
      throw new Error('Invalid arguments for createTable: expected an array with at least one element (CreateTableParams object)');
    }
    if (!args[0] || typeof args[0] !== 'object') {
      throw new Error('Invalid arguments for createTable: first argument must be a CreateTableParams object');
    }
    return this.#currentClient!.createTable(
      args[0] as unknown as CreateTableParams
    );
  }

  public async uploadTable(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('upload table');
    if (!Array.isArray(args) || args.length < 2) {
      throw new Error('Invalid arguments for uploadTable: expected an array with at least two elements (tableName: string, table: Buffer)');
    }
    if (typeof args[0] !== 'string') {
      throw new Error('Invalid arguments for uploadTable: first argument must be a string (table name)');
    }
    if (!Buffer.isBuffer(args[1])) {
      throw new Error('Invalid arguments for uploadTable: second argument must be a Buffer containing Arrow IPC data');
    }
    return this.#currentClient!.uploadTable(args[0] as string, args[1] as Buffer);
  }

  public async updateTables(): Promise<void> {
    this.ensureClientConnected('update tables');
    return this.#currentClient!.updateTables();
  }

  public async deleteTable(args: ArrowParams): Promise<void> {
    this.ensureClientConnected('delete table');
    return this.#currentClient!.deleteTable(args[0] as string);
  }

  // DEAD CODE (EHQ-5): presign now goes through ObjectStorageFileService (HTTP to
  // ehq-server's signed-url endpoint), so nothing routes here anymore. The
  // newton-rs SignResource path is left in place intentionally (not removed in
  // this change) but is currently unused.
  public async presignUrl(args: [string, string, number | undefined]): Promise<string> {
    this.ensureClientConnected('get presigned url');
    const [connectionName, path, duration] = args;
    return this.#currentClient!.presignUrl(connectionName, path, duration);
  }

  public async performDbQuery(args: ArrowParams, context?: ExecutionContext): Promise<Uint8Array> {
    this.ensureClientConnected('perform db query', context);
    const o = optionsOf(args);
    // The options form is a single argument, so only the positional form has a
    // two-argument minimum (EHQ-115).
    this.validateArguments(args, o ? 1 : 2, 'performing db query', context);
    try {
      const connectionName = optionField(o, 'connectionName', args[0]) as string;
      const query = optionField(o, 'query', args[1]) as string;
      const params = toTypedParams(optionField(o, 'params', args[2]), 'performDbQuery');
      this.requireStrings({ connectionName, query }, 'performing db query', context);
      return await this.#currentClient!.performDbQuery(connectionName, query, params);
    } catch (error: unknown) {
      this.handleError(error, 'performing db query', context);
    }
  }

  public async performDbQueryAsJson(args: ArrowParams, context?: ExecutionContext): Promise<string> {
    this.ensureClientConnected('perform db query as JSON', context);
    const o = optionsOf(args);
    this.validateArguments(args, o ? 1 : 2, 'performing db query as JSON', context);
    try {
      const connectionName = optionField(o, 'connectionName', args[0]) as string;
      const query = optionField(o, 'query', args[1]) as string;
      const params = toTypedParams(
        optionField(o, 'params', args[2]),
        'performDbQueryAsJson'
      );
      this.requireStrings(
        { connectionName, query },
        'performing db query as JSON',
        context
      );
      return await this.#currentClient!.performDbQueryAsJson(connectionName, query, params);
    } catch (error: unknown) {
      this.handleError(error, 'performing db query as JSON', context);
    }
  }

  public async createTableFromQuery(args: ArrowParams, context?: ExecutionContext): Promise<object> {
    this.ensureClientConnected('create table from query', context);
    const o = optionsOf(args);
    this.validateArguments(args, o ? 1 : 3, 'creating table from query', context);
    try {
      const tableName = optionField(o, 'tableName', args[0]) as string;
      const connectionName = optionField(o, 'connectionName', args[1]) as string;
      const query = optionField(o, 'query', args[2]) as string;
      const params = toTypedParams(
        optionField(o, 'params', args[3]),
        'createTableFromQuery'
      );
      this.requireStrings(
        { tableName, connectionName, query },
        'creating table from query',
        context
      );
      return await this.#currentClient!.createTableFromQuery(
        tableName, connectionName, query, params
      );
    } catch (error: unknown) {
      this.handleError(error, 'creating table from query', context);
    }
  }

  public async listActions(args: ArrowParams, context?: ExecutionContext): Promise<any[]> {
    this.ensureClientConnected('list actions', context);
    try {
      return await this.#currentClient!.listActions();
    } catch (error: unknown) {
      this.handleError(error, 'listing actions', context);
    }
  }

  public async listFlights(args: ArrowParams, context?: ExecutionContext): Promise<any[]> {
    this.ensureClientConnected('list flights', context);
    try {
      return await this.#currentClient!.listFlights(args[0] as string, args[1] as boolean);
    } catch (error: unknown) {
      this.handleError(error, 'listing flights', context);
    }
  }

  public async getFlightInfo(args: ArrowParams, context?: ExecutionContext): Promise<any> {
    this.ensureClientConnected('get flight info', context);
    this.validateArguments(args, 1, 'getting flight info', context);
    try {
      return await this.#currentClient!.getFlightInfo(args[0] as string);
    } catch (error: unknown) {
      this.handleError(error, 'getting flight info', context);
    }
  }

  public async createTableFromFile(args: ArrowParams, context?: ExecutionContext): Promise<object> {
    this.ensureClientConnected('create table from file', context);
    this.validateArguments(args, 2, 'creating table from file', context);
    try {
      return await this.#currentClient!.createTableFromFile(
        args[0] as string, args[1] as string, args[2] as any
      );
    } catch (error: unknown) {
      this.handleError(error, 'creating table from file', context);
    }
  }
}
